import React, { useContext, useState } from "react";
import styles from "../styles/DeleteAccount.module.css";
import { supabase } from "../lib/supabaseClient";
import { UserContext } from "./context/user/UserContext";
import { useNavigate, Link } from "react-router-dom";
import { deleteUserAccount } from "../utils/accountUtils";
import { showSuccess, showError, showWarning, showInfo } from "../utils/toastUtils";
import { EULA_URL } from "../utils/legal";
import OtpVerification from "./OtpVerification";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { userData, setUserData } = useContext(UserContext);
  const [showModal, setShowModal] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // If user is not logged in, show login prompt instead of redirecting
  if (!userData) {
    return (
      <div className={styles.container}>
        <div className={styles.wrapper}>
          <h1>🔒 Authentication Required</h1>
          <p className={styles.warningText}>
            You need to be logged in to delete your account.
          </p>
          <div className={styles.infoBox}>
            <h3>Why do I need to log in?</h3>
            <p style={{ color: 'var(--text-secondary, #999)', marginTop: '1rem' }}>
              To protect your account security, we require authentication before allowing account deletion. 
              This ensures only you can delete your own account.
            </p>
          </div>
          <button
            className={styles.loginButton}
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const { user } = userData;
  const userId = user.id;
  const userEmail = user.email;

  const handleDeleteRequest = () => {
    if (!agreedToTerms) {
      showWarning("Please agree to the Terms of Service, Privacy Policy, and EULA first");
      return;
    }
    setShowModal(false);
    setShowOtp(true);
  };

  const deleteHandler = async () => {
    setIsDeleting(true);
    showInfo("Deleting your account and all data...");

    try {
      // Verified by email OTP just before this runs.
      const result = await deleteUserAccount(userId);

      if (!result.success) {
        throw new Error(result.error || "Failed to delete account");
      }

      // Sign out the user
      await supabase.auth.signOut();

      // Clear local storage and context
      window.localStorage.removeItem("userData");
      setUserData(null);

      showSuccess("Account deleted successfully. Redirecting...");

      setTimeout(() => {
        navigate("/");
      }, 2000);
    } catch (error) {
      console.error("Error deleting account:", error);
      showError(`Failed to delete account: ${error.message}`);
      setIsDeleting(false);
      setShowOtp(false);
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setShowOtp(false);
    setIsDeleting(false);
  };

  if (showOtp) {
    return (
      <OtpVerification
        email={userEmail}
        shouldCreateUser={false}
        onVerified={deleteHandler}
        onBack={handleCancel}
        title="Verify Your Identity"
        subtitle="To permanently delete your account, enter the 6-digit code we sent to"
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <h1>⚠️ Delete Account</h1>
        <p className={styles.warningText}>
          Deleting your account is permanent and cannot be undone.
        </p>
        <div className={styles.infoBox}>
          <h3>What will be deleted:</h3>
          <ul>
            <li>Your profile information</li>
            <li>All locations you've posted</li>
            <li>Your subscription data</li>
            <li>All your saved preferences</li>
          </ul>
        </div>
        <div className={styles.termsContainer}>
          <input
            type="checkbox"
            id="agreeDelete"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className={styles.termsCheckbox}
          />
          <label htmlFor="agreeDelete" className={styles.termsText}>
            I AGREE to the{" "}
            <Link to="/terms#terms-of-service" target="_blank">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/terms#privacy-policy" target="_blank">
              Privacy Policy
            </Link>
            , and I have read the{" "}
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer">
              End User License Agreement (EULA)
            </a>
            .
          </label>
        </div>
        <button
          className={styles.deleteButton}
          onClick={() => setShowModal(true)}
          disabled={isDeleting || !agreedToTerms}
        >
          Delete My Account
        </button>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCancel}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2>Are you absolutely sure?</h2>
            <p className={styles.modalText}>
              This action <strong>cannot be undone</strong>. This will permanently delete your account and remove all your data from our servers.
            </p>
            <div className={styles.modalButtons}>
              <button
                className={styles.cancelBtn}
                onClick={handleCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={styles.confirmBtn}
                onClick={handleDeleteRequest}
                disabled={isDeleting}
              >
                Yes, Delete My Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeleteAccount;
